import { vehicleModelApi } from "#/api/vehicleModelApi";
import ActionButton from "#/components/buttons/ActionButton";
import {
  showErrorNotification,
  showSuccessNotification,
} from "#/components/notifications";
import DetailPageLayout from "#/components/ui/DetailPageLayout";
import type { CreateVehicleModelPayload } from "#/types/vehicle-model";
import { Accordion, Button, TextInput } from "@mantine/core";
import { useForm } from "@mantine/form";
import { IconPlus, IconTrash } from "@tabler/icons-react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { usePermission } from "#/hooks/usePermission";
import PermissionTooltip from "#/components/ui/PermissionTooltip";

const VehicleModelDetailPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const { canWrite } = usePermission();

  const form = useForm<CreateVehicleModelPayload>({
    initialValues: {
      name: "",
      brand: "",
      model: "",
      size: "",
      customize_columns: {},
    },
    validate: {
      name: (value) => (value ? null : t("vehicleModels.validation.nameRequired")),
      brand: (value) => (value ? null : t("vehicleModels.validation.brandRequired")),
      model: (value) => (value ? null : t("vehicleModels.validation.modelRequired")),
      size: (value) => (value ? null : t("vehicleModels.validation.sizeRequired")),
    },
  });

  const handleSubmit = (values: typeof form.values) => {
    const payload = {
      ...values,
    };

    vehicleModelMutation.mutate(payload);
  };

  const vehicleModelMutation = useMutation({
    mutationFn: async (payload: CreateVehicleModelPayload) => {
      if (id !== "new") {
        return await vehicleModelApi.update(Number(id), payload);
      } else {
        return await vehicleModelApi.create(payload);
      }
    },
    onSuccess: (data) => {
      showSuccessNotification({
        message: id === "new" ? t("vehicleModels.createdSuccess") : t("vehicleModels.updatedSuccess"),
      });
      if (id === "new") {
        void navigate(`/fleet/models/${data.data.id}`, { replace: true });
      } else {
        form.resetDirty();
      }
    },
    onError: (error) => {
      console.log("🚀 ~ VehicleModelDetail ~ error:", error);
      showErrorNotification({
        message: error.message ?? t("common.failedToProcess"),
      });
    },
  });

  useQuery({
    queryKey: ["vehicleModelDetails", id],
    enabled: !!id && id !== "new",
    queryFn: async () => {
      if (!id || id === "new") throw new Error("Invalid vehicleModelId");

      const response = await vehicleModelApi.getById(Number(id));
      if (!response.data) throw new Error("Failed to fetch vehicle model");
      const vehicleModel = response.data;
      form.setValues({
        name: vehicleModel.name ?? "",
        brand: vehicleModel.brand ?? "",
        model: vehicleModel.model ?? "",
        size: vehicleModel.size ?? "",
        customize_columns: { ...(vehicleModel.customize_columns ?? {}) },
      });
      form.resetDirty();
      return vehicleModel;
    },
  });

  return (
    <DetailPageLayout
      title={id !== "new" ? t("vehicleModels.modelDetails") : t("vehicleModels.createModel")}
      navRightSection={
        <div className="w-full flex justify-end gap-2 items-center">
          <Button
            color="black"
            variant="outline"
            type="reset"
            disabled={!form.isDirty()}
            onClick={() => {
              form.reset();
            }}
            size="xs"
          >
            {t("common.clear")}
          </Button>

          <PermissionTooltip disabled={!canWrite()}>
            <Button
              color="black"
              variant="filled"
              loading={vehicleModelMutation.isPending}
              disabled={!form.isDirty() || !canWrite()}
              onClick={() => {
                form.onSubmit(handleSubmit)();
              }}
              size="xs"
            >
              {t("common.save")}
            </Button>
          </PermissionTooltip>
        </div>
      }
    >
      <div>
        <form onSubmit={form.onSubmit(handleSubmit)}>
          <Accordion
            variant="separated"
            defaultValue={["basic", "customize_columns"]}
            multiple
          >
            <Accordion.Item value="basic">
              <Accordion.Control>{t("common.basicInformation")}</Accordion.Control>
              <Accordion.Panel>
                <div className="w-full flex flex-col gap-4">
                  <TextInput
                    label={t("vehicleModels.form.name")}
                    withAsterisk
                    disabled={!canWrite()}
                    placeholder={t("vehicleModels.form.namePlaceholder")}
                    {...form.getInputProps("name")}
                  />

                  <div className="w-full grid grid-cols-3 gap-4">
                    <TextInput
                      label={t("vehicleModels.form.brand")}
                      withAsterisk
                      disabled={!canWrite()}
                      placeholder={t("vehicleModels.form.brandPlaceholder")}
                      {...form.getInputProps("brand")}
                    />

                    <TextInput
                      label={t("vehicleModels.form.model")}
                      withAsterisk
                      disabled={!canWrite()}
                      placeholder={t("vehicleModels.form.modelPlaceholder")}
                      {...form.getInputProps("model")}
                    />

                    <TextInput
                      label={t("vehicleModels.form.size")}
                      withAsterisk
                      disabled={!canWrite()}
                      placeholder={t("vehicleModels.form.sizePlaceholder")}
                      {...form.getInputProps("size")}
                    />
                  </div>
                </div>
              </Accordion.Panel>
            </Accordion.Item>

            <Accordion.Item value="customize_columns">
              <Accordion.Control>
                <div className="flex items-center gap-2">
                  <span>{t("common.customFields")}</span>
                  <PermissionTooltip disabled={!canWrite()}>
                    <ActionButton
                      color="black"
                      size="xs"
                      icon={IconPlus}
                      onClick={(e) => {
                        e.stopPropagation();
                        const existing = { ...form.values.customize_columns };
                        let newKey = `field_${Object.keys(existing).length + 1}`;
                        while (newKey in existing) {
                          newKey += "_";
                        }
                        form.setFieldValue("customize_columns", {
                          ...existing,
                          [newKey]: "",
                        });
                      }}
                      disabled={!canWrite()}
                    />
                  </PermissionTooltip>
                </div>
              </Accordion.Control>

              <Accordion.Panel>
                {Object.entries(form.values.customize_columns ?? {}).length <=
                0 ? (
                  <div>{t("common.addCustomFieldHint")}</div>
                ) : (
                  <div className="w-full flex flex-col gap-2">
                    {Object.entries(form.values.customize_columns ?? {}).map(
                      ([key, value], index) => {
                        const fieldId = `${key}_${index}`;

                        return (
                          <div
                            key={fieldId}
                            className="w-full grid grid-cols-[auto_1fr_1fr_auto] gap-2 items-center"
                          >
                            <span className="bg-black/5 font-bold text-black/70 rounded-full p-2 w-8 h-8 flex items-center justify-center">
                              <span>{index + 1}</span>
                            </span>
                            <TextInput
                              defaultValue={key}
                              placeholder={t("common.fieldName")}
                              disabled={!canWrite()}
                              onBlur={(event) => {
                                const newKey = event.currentTarget.value.trim();
                                if (!newKey || newKey === key) return;

                                const updated = {
                                  ...form.values.customize_columns,
                                };
                                const val = updated[key];
                                delete updated[key];

                                if (newKey in updated) return;

                                updated[newKey] = val;
                                form.setFieldValue("customize_columns", updated);
                              }}
                            />

                            <TextInput
                              value={value}
                              placeholder={t("common.fieldValue")}
                              disabled={!canWrite()}
                              onChange={(event) => {
                                form.setFieldValue(
                                  `customize_columns.${key}`,
                                  event.currentTarget.value
                                );
                              }}
                            />

                            <PermissionTooltip disabled={!canWrite()}>
                              <ActionButton
                                icon={IconTrash}
                                variant="white"
                                color="red.5"
                                onClick={() => {
                                  const updated = {
                                    ...form.values.customize_columns,
                                  };
                                  delete updated[key];
                                  form.setFieldValue("customize_columns", updated);
                                }}
                                disabled={!canWrite()}
                              />
                            </PermissionTooltip>
                          </div>
                        );
                      }
                    )}
                  </div>
                )}
              </Accordion.Panel>
            </Accordion.Item>
          </Accordion>
        </form>
      </div>
    </DetailPageLayout>
  );
};

export default VehicleModelDetailPage;
